"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _reactWindow = require("react-window");
var _Checkbox = _interopRequireDefault(require("./Checkbox"));
var _Input = _interopRequireDefault(require("../Input/Input"));
var _Button = _interopRequireDefault(require("../Button/Button"));
var _Dropdown = _interopRequireDefault(require("../Dropdown/Dropdown"));
var _useFormElementValidation = _interopRequireDefault(require("../../hooks/useFormElementValidation"));
var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));
var _useLabel2 = _interopRequireDefault(require("../../hooks/useLabel"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
/**
 * The CheckboxGroup component offers a convenient way to display multiple Checkbox components
 */
var CheckboxMultiple = function CheckboxMultiple(props) {
  var options = props.options,
    checked = props.checked,
    controlledId = props.id,
    controlledName = props.name,
    inputLabel = props.inputLabel,
    inputSelectionLabel = props.inputSelectionLabel,
    inputAllSelectedLabel = props.inputAllSelectedLabel,
    searchLabel = props.searchLabel,
    selectButtonLabel = props.selectButtonLabel,
    clearButtonLabel = props.clearButtonLabel,
    helper = props.helper,
    inline = props.inline,
    displayAsButton = props.displayAsButton,
    checkboxesClassName = props.checkboxesClassName,
    size = props.size,
    color = props.color,
    outline = props.outline,
    required = props.required,
    disabled = props.disabled,
    placeholder = props.placeholder,
    atLeast = props.atLeast,
    atMost = props.atMost,
    onChange = props.onChange,
    onError = props.onError,
    className = props.className,
    displaySearchBar = props.displaySearchBar,
    customMessage = props.customMessage;
  var id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;
  var name = controlledName || id;
  var _useLabel = (0, _useLabel2.default)(inputLabel, _extends({}, props, {
      id: id,
      required: required
    })),
    label = _useLabel.label;
  var _React$useState = _react.default.useState(checked || []),
    stateChecked = _React$useState[0],
    setChecked = _React$useState[1];
  var _React$useState2 = _react.default.useState(''),
    searchTerms = _React$useState2[0],
    setSearchTerms = _React$useState2[1];
  var _React$useState3 = _react.default.useState(options || []),
    stateOptions = _React$useState3[0],
    setOptions = _React$useState3[1];
  var _React$useState4 = _react.default.useState(false),
    error = _React$useState4[0],
    setError = _React$useState4[1];
  var searchInput = _react.default.useRef();
  var _useFormElementValida = (0, _useFormElementValidation.default)(stateChecked, {
      required: required,
      atLeast: atLeast,
      atMost: atMost,
      customMessage: customMessage
    }),
    errors = _useFormElementValida[0],
    methods = _useFormElementValida[1];
  _react.default.useEffect(function () {
    var newChecked = checked.filter(function (c) {
      return stateOptions.map(function (o) {
        return o.id;
      }).includes(c);
    });
    setChecked(newChecked || []);
  }, [checked, stateOptions]);
  _react.default.useEffect(function () {
    setError(errors.length > 0);
    if (typeof onError === 'function') {
      onError({
        name: name,
        errors: errors.length ? errors : []
      });
    }
  }, [errors, onError, name]);
  _react.default.useEffect(function () {
    var newOptions = options.filter(function (option) {
      return option.label.toLowerCase().includes(searchTerms.toLowerCase());
    });
    setOptions(newOptions);
  }, [options, searchTerms]);
  var handleChange = function handleChange(checkedCheckbox, ref, event) {
    var checkboxId = event.target.id;
    var newStateChecked = stateChecked;
    if (checkedCheckbox) {
      if (atMost && stateChecked && stateChecked.length === atMost) {
        newStateChecked.shift();
      }
      newStateChecked.push(checkboxId);
    } else {
      newStateChecked = stateChecked.filter(function (value) {
        return value !== checkboxId;
      });
    }
    setChecked(newStateChecked);
    methods.onChangeCheckboxGroup(newStateChecked);
    if (typeof onChange === 'function') {
      onChange(newStateChecked, name, event);
    }
  };
  var handleBlur = function handleBlur() {
    return methods.onChangeCheckboxGroup(stateChecked);
  };
  var handleSelectAll = function handleSelectAll() {
    var newStateChecked = stateOptions.filter(function (option) {
      return !option.disabled;
    }).map(function (option) {
      return option.id;
    });
    var concatChecked = Array.from(new Set(stateChecked.concat(newStateChecked)));
    setChecked(concatChecked);
    methods.onChangeCheckboxGroup(concatChecked);
    if (typeof onChange === 'function') {
      onChange(concatChecked);
    }
  };
  var handleUnselectAll = function handleUnselectAll() {
    if (stateChecked.length > 0) {
      setChecked([]);
      methods.onChangeCheckboxGroup([]);
      if (typeof onChange === 'function') {
        onChange([]);
      }
    }
  };
  var customDisplay = function customDisplay() {
    if (stateChecked.length) {
      if (stateChecked.length === options.length) {
        return inputAllSelectedLabel;
      }
      if (stateChecked.length > 1) {
        return stateChecked.length + "/" + options.length + " " + inputSelectionLabel;
      }
      var opt = options.find(function (option) {
        return String(option.id) === String(stateChecked[0]) || String(option.key) === String(stateChecked[0]);
      });
      return opt ? "" + opt.label : '';
    }
    return '';
  };
  var getIsSelectAllDisabled = function getIsSelectAllDisabled() {
    if (atMost === 0 || atMost && options && atMost < options.length) {
      return true;
    }
    if (options && stateChecked) {
      return options.length === stateChecked.length;
    }
    return false;
  };
  var handleClick = function handleClick(event, isOpen) {
    if (!isOpen) {
      searchInput.current && searchInput.current.querySelector('input').focus();
    }
  };
  var classesCheckboxGroup = (0, _clsx.default)('uk-checkbox-group', (helper.length > 0 || error) && 'has-helper', error && 'has-error', inline && 'uk-checkbox-group--inline', className);
  var classCheckboxGroupInput = (0, _clsx.default)(stateChecked && stateChecked.length > 0 ? 'value-selected' : 'no-value-selected', required && stateChecked && stateChecked.length < 1 && 'has-error');
  return /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-checkbox-multiple"
  }, /*#__PURE__*/_react.default.createElement(_Dropdown.default, {
    tabIndex: "-1",
    withIcon: false,
    onClick: handleClick
    // mountOnOpen={mountOnOpen}
    ,
    disabled: disabled,
    trigger: /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_Input.default, {
      disabled: !options || !options.length || disabled,
      placeholder: placeholder,
      clearable: !disabled,
      readOnly: true,
      tabIndex: "0",
      className: classCheckboxGroupInput,
      value: customDisplay(),
      label: label,
      onChange: handleUnselectAll,
      "aria-label": "Display multiple checkboxes",
      "aria-describedby": (name || id) + "-helper-text",
      id: id,
      icon: disabled ? undefined : 'dropdownIndicator'
    }))
  }, displaySearchBar && /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-checkbox-multiple__search-bar",
    ref: searchInput
  }, /*#__PURE__*/_react.default.createElement(_Input.default, {
    className: "input-search",
    onChange: function onChange(searchValue) {
      return setSearchTerms(searchValue);
    },
    clearable: true,
    placeholder: searchLabel,
    icon: "search",
    "aria-label": "Search"
  })), /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-checkbox-multiple__select-options"
  }, /*#__PURE__*/_react.default.createElement(_Button.default, {
    onClick: handleSelectAll,
    displayAsBlock: true,
    className: "select-all",
    color: "secondary",
    label: selectButtonLabel,
    disabled: getIsSelectAllDisabled(),
    size: "small"
  }), /*#__PURE__*/_react.default.createElement(_Button.default, {
    onClick: handleUnselectAll,
    displayAsBlock: true,
    className: "unselect-all",
    color: "secondary",
    label: clearButtonLabel,
    disabled: !stateChecked.length,
    size: "small"
  })), /*#__PURE__*/_react.default.createElement("div", {
    className: classesCheckboxGroup + " checkbox-list-container"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-checkbox-group__checkboxes"
  }, stateOptions.length > 7 ? /*#__PURE__*/_react.default.createElement(_reactWindow.FixedSizeList, {
    height: 220,
    itemCount: stateOptions.length,
    itemSize: 32,
    itemData: stateOptions
  }, function (props) {
    // Access the items array using the "data" prop:
    var item = props.data[props.index];
    return /*#__PURE__*/_react.default.createElement("div", {
      style: props.style
    }, /*#__PURE__*/_react.default.createElement(_Checkbox.default, {
      key: item.id || id + "_" + item.index,
      name: item.name || name + "_" + item.index,
      id: item.id || id + "_" + item.index,
      label: item.label,
      checked: stateChecked.includes(item.id) || stateChecked.includes(id + "_" + item.index),
      disabled: item.disabled || false,
      onChange: handleChange,
      onBlur: handleBlur,
      color: color,
      size: size,
      outline: outline,
      value: item.value,
      className: checkboxesClassName,
      displayAsButton: displayAsButton
    }));
  }) : stateOptions.map(function (item, i) {
    return /*#__PURE__*/_react.default.createElement(_Checkbox.default, {
      key: item.id || id + "_" + i,
      name: item.name || name + "_" + i,
      id: item.id || id + "_" + i,
      label: item.label,
      checked: stateChecked.includes(item.id) || stateChecked.includes(id + "_" + i),
      disabled: item.disabled || false,
      onChange: handleChange,
      onBlur: handleBlur,
      color: color,
      size: size,
      outline: outline,
      value: item.value,
      className: checkboxesClassName,
      displayAsButton: displayAsButton
    });
  })), (helper.length > 0 || error) && /*#__PURE__*/_react.default.createElement("small", {
    className: "uk-form-item__helper",
    id: (name || id) + "-helper-text"
  }, error ? errors : helper), atMost && atMost === 1 && /*#__PURE__*/_react.default.createElement("small", {
    className: "uk-form-item__helper"
  }, "maximum 1 option"), atMost && atMost > 1 && /*#__PURE__*/_react.default.createElement("small", {
    className: "uk-form-item__helper"
  }, "maximum " + atMost + " options"))));
};
CheckboxMultiple.propTypes = {
  /**
   * Array of Checkboxes props
   */
  options: _propTypes.default.arrayOf(_propTypes.default.shape),
  /**
   * Ids of the Checkboxes to set as selected
   */
  checked: _propTypes.default.arrayOf(_propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number])),
  /**
   * Prefix for the Checkboxes id (unless it has been set in the options prop)
   */
  id: _propTypes.default.string,
  /**
   * Prefix for the Checkboxes name (unless it has been set in the options prop)
   */
  name: _propTypes.default.string,
  /**
   * Label of the CheckboxeGroup
   */
  inputLabel: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Label of the Search-bar
   */
  searchLabel: _propTypes.default.string,
  /**
   * Label of select all button
   */
  selectButtonLabel: _propTypes.default.string,
  /**
   * Label of clear button
   */
  clearButtonLabel: _propTypes.default.string,
  /**
   * Layout of the CheckboxeGroup
   */
  inline: _propTypes.default.bool,
  /**
   * Change the display of the CheckboxeGroup
   */
  displayAsButton: _propTypes.default.bool,
  /**
   * ClassName to apply to each Checkbox
   */
  checkboxesClassName: _propTypes.default.string,
  /**
   * PlaceHolder of the Checkboxes
   */
  placeholder: _propTypes.default.string,
  /**
   * Size of the Checkboxes
   */
  size: _propTypes.default.oneOf(['tiny', 'small', 'default', 'large', 'big']),
  /**
   * Background-color of selected Checkboxes (or checkmark color if outline)
   */
  color: _propTypes.default.oneOf(['default', 'primary', 'secondary', 'success', 'alert', 'warning', 'white']),
  /**
   * Invert styling colors of the Checkboxes
   */
  outline: _propTypes.default.bool,
  /**
   * Helper text, is replaced by a message in case of error
   */
  helper: _propTypes.default.string,
  /**
   * Form-validation: set the CheckboxGroup as required
   */
  required: _propTypes.default.bool,
  disabled: _propTypes.default.bool,
  /**
   * Form-validation: set the minimal number of Checkboxes that must be checked
   */
  atLeast: _propTypes.default.number,
  /**
   * Form-validation: set the maximal number of Checkboxes that can be checked
   */
  atMost: _propTypes.default.number,
  /**
   * Callback function onChange
   * Only handle when onClickClear (clearable: handleOnClear) beacause Input is in readOnly mode
   */
  onChange: _propTypes.default.func,
  /**
   * Callback function onError
   */
  onError: _propTypes.default.func,
  /**
   * Display a search input to allow filter the checkboxes
   */
  displaySearchBar: _propTypes.default.bool,
  /**
   * Add a custom message when custom validation is used
   */
  customMessage: _propTypes.default.object,
  /**
   * Label of the Selection Checkboxe
   */
  inputSelectionLabel: _propTypes.default.string,
  /**
   * Add a custom label message for all input selected
   */
  inputAllSelectedLabel: _propTypes.default.string
};
CheckboxMultiple.defaultProps = {
  inline: false,
  displayAsButton: false,
  size: 'default',
  color: 'default',
  outline: false,
  helper: '',
  required: false,
  disabled: false,
  displaySearchBar: false,
  inputSelectionLabel: 'selection',
  inputAllSelectedLabel: 'All selected'
  // mountOnOpen: false
};
var _default = exports.default = CheckboxMultiple;